import React, { useEffect, useState, useCallback } from 'react';
import { familyApi } from '../services/family';
import Select from './Select';
import '../styles/forms.css';
import '../styles/FamilyPage.css';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

// Pages the admin can grant to a member. Must match the backend ALL_PAGES
// (minus 'home' which is always visible).
const PAGE_OPTIONS = [
  { key: 'investments', label: 'Investments' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'import-transactions', label: 'Import Statement' },
  { key: 'manage-accounts', label: 'Manage Accounts' },
  { key: 'manage-subjects', label: 'Manage Subjects' },
  { key: 'manage-categories', label: 'Manage Categories' },
  { key: 'manage-recurrences', label: 'Manage Recurrences' },
];

const FamilyPage = () => {
  const [familyData, setFamilyData] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // panel state
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');

  // invitation form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [createdInvite, setCreatedInvite] = useState(null);

  // confirm/manage member modal
  const [managingMember, setManagingMember] = useState(null);
  const [memberRole, setMemberRole] = useState('member');
  const [memberAccounts, setMemberAccounts] = useState(new Set());
  const [memberPages, setMemberPages] = useState(new Set());

  // pending invitation to confirm
  const [confirmingInvite, setConfirmingInvite] = useState(null);
  const [confirmRole, setConfirmRole] = useState('member');
  const [confirmAccounts, setConfirmAccounts] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [fam, accts] = await Promise.all([
        familyApi.get(),
        familyApi.familyAccounts().catch(() => []),
      ]);
      setFamilyData(fam);
      setAllAccounts((accts || []).filter((a) => a.is_open));
      // Update the gate flag so ProtectedRoute knows whether to restrict navigation.
      const hasFamily = !!fam?.family;
      localStorage.setItem('hasFamily', String(hasFamily));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load family.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = familyData?.role === 'admin';

  // --- create family ---
  const handleCreateFamily = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await familyApi.create(newFamilyName.trim());
      setShowCreateFamily(false);
      setNewFamilyName('');
      // Full reload so the app re-syncs allowedPages/hasFamily — the menu
      // only appears after this (and the tour resumes at the saved step).
      window.location.reload();
      return;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create family.');
    }
  };

  // --- rename family ---
  const handleRenameFamily = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await familyApi.rename(editNameValue.trim());
      setEditingName(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename family.');
    }
  };

  // --- create invitation ---
  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setCreatedInvite(null);
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      setError('A valid email is required.');
      return;
    }
    try {
      const inv = await familyApi.createInvitation(inviteEmail.trim(), inviteRole);
      setCreatedInvite(inv);
      setInviteEmail('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invitation.');
    }
  };

  // --- revoke invitation ---
  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    setError('');
    try {
      await familyApi.revokeInvitation(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke invitation.');
    }
  };

  // --- open confirm panel for an accepted invitation ---
  const openConfirm = (inv) => {
    setConfirmingInvite(inv);
    setConfirmRole(inv.proposed_role || 'member');
    setConfirmAccounts(new Set());
    setMessage('');
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!confirmingInvite) return;
    setError('');
    setSaving(true);
    try {
      await familyApi.confirmInvitation(confirmingInvite.id, {
        role: confirmRole,
        account_ids: confirmRole === 'member' ? Array.from(confirmAccounts) : [],
      });
      setConfirmingInvite(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm member.');
    } finally {
      setSaving(false);
    }
  };

  // --- open manage panel for a member ---
  const openManage = async (member) => {
    setManagingMember(member);
    setMemberRole(member.role);
    setMemberAccounts(new Set(member.granted_account_ids || []));
    setMemberPages(new Set());
    setMessage('');
    if (member.role === 'member') {
      try {
        const pages = await familyApi.memberPages(member.user_id);
        setMemberPages(new Set(pages.pages || []));
      } catch { /* ignore — empty set is fine */ }
    }
  };

  const handleSaveMember = async (e) => {
    e.preventDefault();
    if (!managingMember) return;
    setError('');
    setSaving(true);
    try {
      await familyApi.updateMember(managingMember.user_id, {
        role: memberRole,
        account_ids: memberRole === 'member' ? Array.from(memberAccounts) : [],
      });
      if (memberRole === 'member') {
        await familyApi.updateMemberPages(managingMember.user_id, Array.from(memberPages));
      }
      setManagingMember(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the family?')) return;
    setError('');
    try {
      await familyApi.removeMember(userId);
      setManagingMember(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member.');
    }
  };

  const [saving, setSaving] = useState(false);

  const toggleAccount = (id, set, setter) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => setMessage('Link copied to clipboard.'));
    } else {
      // Fallback for non-secure contexts (HTTP): execCommand('copy')
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand('copy');
        setMessage('Link copied to clipboard.');
      } catch {
        setMessage('Copy this link manually.');
      }
      document.body.removeChild(ta);
    }
  };

  if (loading) return <div className="card"><p className="family-hint">Loading…</p></div>;

  // No family
  if (!familyData?.family) {
    return (
      <div className="card">
        <h2 className="page-title">Family</h2>
        <p className="family-hint">
          You are not part of a family yet. Create one to invite family members and share accounts.
        </p>
        {error && <div className="error">{error}</div>}
        {showCreateFamily ? (
          <form onSubmit={handleCreateFamily} className="form" data-tour="create-family">
            <div className="form-group">
              <label>Family name
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="e.g. The Smiths"
                  required
                />
              </label>
            </div>
            <div className="form-buttons">
              <button type="button" className="btn btn--secondary" onClick={() => setShowCreateFamily(false)}>Cancel</button>
              <button type="submit" className="btn btn--primary">Create Family</button>
            </div>
          </form>
        ) : (
          <div className="form-buttons" data-tour="create-family-cta">
            <button className="btn btn--primary" onClick={() => setShowCreateFamily(true)}>Create a Family</button>
          </div>
        )}
        <hr className="separator" />
        <JoinWithToken onJoined={load} />
      </div>
    );
  }

  const acceptedInvites = (familyData.invitations || []).filter((i) => i.status === 'accepted');
  const pendingInvites = (familyData.invitations || []).filter((i) => i.status === 'pending');

  return (
    <div className="card">
      <div className="family-header" data-tour="family-name">
        <h2 className="page-title">
          {editingName && isAdmin ? (
            <input
              type="text"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              onBlur={handleRenameFamily}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFamily(e); if (e.key === 'Escape') setEditingName(false); }}
              autoFocus
            />
          ) : (
            <span
              onDoubleClick={isAdmin ? () => { setEditNameValue(familyData.family.name); setEditingName(true); } : undefined}
              title={isAdmin ? 'Double-click to rename' : ''}
            >
              {familyData.family.name}
            </span>
          )}
        </h2>
        <span className={`role-badge role-${familyData.role}`}>{familyData.role === 'admin' ? 'Admin' : 'Member'}</span>
      </div>

      {error && <div className="error">{error}</div>}
      {message && <p className="family-saved">{message}</p>}

      {/* Members */}
      <section className="family-section">
        <h3 className="family-section-title">Members</h3>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Accounts</th>{isAdmin && <th>Actions</th>}</tr>
            </thead>
            <tbody>
              {(familyData.members || []).map((m) => (
                <tr key={m.user_id}>
                  <td>{m.display_name}</td>
                  <td>{m.email}</td>
                  <td><span className={`role-badge role-${m.role}`}>{m.role === 'admin' ? 'Admin' : 'Member'}</span></td>
                  <td>
                    {m.role === 'admin'
                      ? 'All'
                      : (m.granted_account_ids || []).length + ' account(s)'}
                  </td>
                  {isAdmin && (
                    <td>
                      <button className="btn btn--ghost" onClick={() => openManage(m)}>Manage</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Invitations needing confirmation */}
      {isAdmin && (
        <section className="family-section">
          <h3 className="family-section-title">Awaiting Confirmation</h3>
          {acceptedInvites.length > 0 ? (
            <>
              <p className="family-hint">These people have accepted your invitation. Confirm them and assign accounts.</p>
              <div className="family-invite-list">
                {acceptedInvites.map((inv) => (
                  <div key={inv.id} className="family-invite-card">
                    <div className="family-invite-info">
                      <strong>{inv.invited_email}</strong> — accepted {formatDate(inv.accepted_at)}
                    </div>
                    <button className="btn btn--primary" onClick={() => openConfirm(inv)}>Confirm & Assign</button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="family-hint">No one is waiting for confirmation. <button type="button" className="link-button" onClick={() => load()}>Refresh</button></p>
          )}
        </section>
      )}

      {/* Pending invitations (admin) */}
      {isAdmin && (
        <section className="family-section">
          <h3 className="family-section-title">Invite a Family Member</h3>
          <form onSubmit={handleCreateInvitation} className="form family-invite-form">
            <div className="form-group-row">
              <div className="form-group">
                <label>Email
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="family@example.com"
                    required
                  />
                </label>
              </div>
              <div className="form-group">
                <label>Role
                  <Select
                    options={ROLE_OPTIONS}
                    onSelect={(o) => setInviteRole(o.value)}
                    value={inviteRole}
                  />
                </label>
              </div>
            </div>
            <div className="form-buttons">
              <button type="submit" className="btn btn--primary">Create Invitation</button>
            </div>
          </form>

          {createdInvite && (
            <div className="family-share-box">
              <p className="family-hint">Share this link with <strong>{createdInvite.invited_email}</strong>:</p>
              <div className="family-share-link">
                <input type="text" readOnly value={createdInvite.share_url} onFocus={(e) => e.target.select()} />
                <button className="btn btn--secondary" onClick={() => copyToClipboard(createdInvite.share_url)}>Copy</button>
              </div>
              <p className="family-hint family-token">Token: <code>{createdInvite.token}</code></p>
              <p className="family-hint">Expires {formatDate(createdInvite.expires_at)}</p>
            </div>
          )}

          {pendingInvites.length > 0 && (
            <div className="family-invite-list">
              {pendingInvites.map((inv) => (
                <div key={inv.id} className="family-invite-card">
                  <div className="family-invite-info">
                    <strong>{inv.invited_email}</strong> — {inv.proposed_role} — expires {formatDate(inv.expires_at)}
                  </div>
                  <div className="family-share-link">
                    <input type="text" readOnly value={inv.share_url} onFocus={(e) => e.target.select()} />
                    <button className="btn btn--secondary" onClick={() => copyToClipboard(inv.share_url)}>Copy</button>
                  </div>
                  <button className="btn btn--ghost" onClick={() => handleRevoke(inv.id)}>Revoke</button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Member view: just show their accounts */}
      {!isAdmin && (
        <section className="family-section">
          <h3 className="family-section-title">Your Accounts</h3>
          <p className="family-hint">
            You have access to {(familyData.members || []).find((m) => m.role === 'member')?.granted_account_ids?.length || 0} account(s).
          </p>
        </section>
      )}

      <JoinWithToken onJoined={load} />

      {/* Confirm & assign modal */}
      {confirmingInvite && (
        <div className="family-modal-backdrop" onClick={() => setConfirmingInvite(null)} aria-hidden="true" />
      )}
      {confirmingInvite && (
        <aside className="edit-panel create-panel family-modal" role="dialog" aria-label="Confirm member">
          <div className="panel-header">
            <h3>Confirm {confirmingInvite.invited_email}</h3>
            <button type="button" className="btn btn--icon" onClick={() => setConfirmingInvite(null)}>×</button>
          </div>
          <form onSubmit={handleConfirm} className="form">
            <div className="form-group">
              <label>Role
                <Select
                  options={ROLE_OPTIONS}
                  onSelect={(o) => setConfirmRole(o.value)}
                  value={confirmRole}
                />
              </label>
            </div>
            {confirmRole === 'member' && (
              <div className="form-group">
                <label>Granted accounts</label>
                <div className="family-account-checklist">
                  {allAccounts.map((a) => (
                    <label key={a.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={confirmAccounts.has(a.id)}
                        onChange={() => toggleAccount(a.id, confirmAccounts, setConfirmAccounts)}
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">{a.name} ({a.type})</span>
                    </label>
                  ))}
                  {allAccounts.length === 0 && <p className="family-hint">No accounts available.</p>}
                </div>
              </div>
            )}
            {confirmRole === 'admin' && (
              <p className="family-hint">Admins have access to all accounts by default.</p>
            )}
            <div className="form-buttons">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmingInvite(null)}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Confirm & Add'}</button>
            </div>
          </form>
        </aside>
      )}

      {/* Manage member modal */}
      {managingMember && (
        <div className="family-modal-backdrop" onClick={() => setManagingMember(null)} aria-hidden="true" />
      )}
      {managingMember && (
        <aside className="edit-panel create-panel family-modal" role="dialog" aria-label="Manage member">
          <div className="panel-header">
            <h3>Manage {managingMember.display_name}</h3>
            <button type="button" className="btn btn--icon" onClick={() => setManagingMember(null)}>×</button>
          </div>
          <form onSubmit={handleSaveMember} className="form">
            <div className="form-group">
              <label>Role
                <Select
                  options={ROLE_OPTIONS}
                  onSelect={(o) => setMemberRole(o.value)}
                  value={memberRole}
                />
              </label>
            </div>
            {memberRole === 'member' && (
              <div className="form-group">
                <label>Granted accounts</label>
                <div className="family-account-checklist">
                  {allAccounts.map((a) => (
                    <label key={a.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={memberAccounts.has(a.id)}
                        onChange={() => toggleAccount(a.id, memberAccounts, setMemberAccounts)}
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">{a.name} ({a.type})</span>
                    </label>
                  ))}
                  {allAccounts.length === 0 && <p className="family-hint">No accounts available.</p>}
                </div>
              </div>
            )}
            {memberRole === 'member' && (
              <div className="form-group">
                <label>Granted pages</label>
                <div className="family-account-checklist">
                  {PAGE_OPTIONS.map((p) => (
                    <label key={p.key} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={memberPages.has(p.key)}
                        onChange={() => toggleAccount(p.key, memberPages, setMemberPages)}
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {memberRole === 'admin' && (
              <p className="family-hint">Admins have access to all accounts by default.</p>
            )}
            <div className="form-buttons">
              <button type="button" className="btn btn--secondary" onClick={() => setManagingMember(null)}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
            {managingMember.user_id !== familyData.family.admin_id && (
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => handleRemoveMember(managingMember.user_id)}
              >
                Remove from family
              </button>
            )}
          </form>
        </aside>
      )}
    </div>
  );
};

// "Join a family with a token" widget shown at the bottom of the family page
const JoinWithToken = ({ onJoined }) => { // eslint-disable-line react/no-unused-prop-types
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    if (!token.trim()) {
      setError('Paste an invitation token or link.');
      return;
    }
    let t = token.trim();
    // extract token from a URL if the user pasted the full share link
    const match = t.match(/[?&]token=([^&]+)/);
    if (match) t = decodeURIComponent(match[1]);
    setActing(true);
    try {
      await familyApi.lookupInvitation(t);
      await familyApi.acceptInvitation(t);
      // Clear this invitation from the pending list
      const remaining = (JSON.parse(localStorage.getItem('pendingFamilyInvitations') || '[]'))
        .filter((i) => i.token !== t);
      localStorage.setItem('pendingFamilyInvitations', JSON.stringify(remaining));
      // Full reload so the app re-syncs (tour resumes at the saved step).
      window.location.reload();
      return;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation.');
    } finally {
      setActing(false);
    }
  };

  return (
    <section className="family-section">
      <h3 className="family-section-title">Join a Family</h3>
      <p className="family-hint">Have an invitation token or link? Paste it here.</p>
      <form onSubmit={handleJoin} className="form family-join-form">
        {error && <div className="error">{error}</div>}
        <div className="form-group-row">
          <div className="form-group" style={{ flex: 1 }}>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Token or invitation link"
            />
          </div>
          <div className="form-group">
            <button type="submit" className="btn btn--primary" disabled={acting}>{acting ? '…' : 'Accept'}</button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default FamilyPage;